'use client'
import React from 'react'
import { isMobile } from 'react-device-detect'

import PurchaseCarNum from '@/components/purchase/components/shared/purchaseCarNum/view'
import usePurchaseCarNumViewModel from '@/components/purchase/components/shared/purchaseCarNum/viewModel'
import PurchaseCompleteView from '@/components/purchase/components/shared/purchaseComplete/view'
import PurchaseConfirmInfoView from '@/components/purchase/components/shared/purchaseConfirmInfo/view'
import PurchaseHeader from '@/components/purchase/components/shared/purchaseHeader/view'
import usePurchaseHeaderViewModel from '@/components/purchase/components/shared/purchaseHeader/viewModel'
import PurchaseIframe from '@/components/purchase/components/shared/purchaseIframe/view'
import PurchaseInComplete from '@/components/purchase/components/shared/purchaseInComplete/view'
import PurchasePayment from '@/components/purchase/components/shared/purchasePayment/view'
import PurchasePhoneNum from '@/components/purchase/components/shared/purchasePhoneNum/view'
import usePurchasePhoneNumVewModel from '@/components/purchase/components/shared/purchasePhoneNum/viewModel'
import PurchaseTerms from '@/components/purchase/components/shared/purchaseTerms/view'
import SharedEnteringTime from '@/components/purchase/components/sharedParkinglot/components/enteringTime/view'
import useSharedEnteringTimeViewModel from '@/components/purchase/components/sharedParkinglot/components/enteringTime/viewModel'
import useSharedParkinglotPurchaseViewModel from '@/components/purchase/components/sharedParkinglot/viewModel'

import type { SharedParkinglotDetail } from '@/api/server/detail/dto/detailDto'
import Button from '@/shared/components/button/Button'
import Cover from '@/shared/components/Cover'
import Icons from '@/shared/components/icon/Icons'
import Confirm from '@/shared/components/modal/Confirm'
import Modal from '@/shared/components/modal/Modal'
import Toast from '@/shared/components/Toast'
import { Text } from '@/shared/components/typography'
import { generateParkingEndTime } from '@/shared/utils/date'

const SharedParkinglotPurchase = ({ data }: { data: SharedParkinglotDetail }) => {
  const { states: enteringTimeStates, handlers: enteringTimeHandlers } = useSharedEnteringTimeViewModel(data)

  const { states: carNumStates, handlers: carNumHandlers } = usePurchaseCarNumViewModel({
    isPartner: false
  })

  const {
    states: phoneNumStates,
    setters: phoneNumSetters,
    handlers: phoneNumHandlers
  } = usePurchasePhoneNumVewModel({
    isPartner: false
  })

  const { states: purchaseStates, handlers: purchaseHandlers } = useSharedParkinglotPurchaseViewModel(
    data,
    carNumStates.carNum,
    enteringTimeStates.enteringTime,
    enteringTimeStates.price,
    phoneNumStates.token,
    phoneNumStates.authCode,
    phoneNumSetters.setIsConfirmOpen
  )

  const { states: headersStates, handlers: headersHandlers } = usePurchaseHeaderViewModel()
  const confirmTitle = (
    <div className="pb-5 pt-6 font-medium">
      <Text level="text1" weight="medium" color="text090">
        주차권 구매를 그만하시겠어요?
      </Text>
    </div>
  )

  const footer = (
    <div className="flex w-full gap-2">
      <Button text="나가기" background="bg020" color="text090" onClick={headersHandlers.handleCloseClick} />
      <Button text="계속하기" background="bg030" color="text010" onClick={headersHandlers.clickFooterContinueBtn} />
    </div>
  )

  const confirmInfoProps = {
    isOpen: phoneNumStates.isConfirmOpen,
    onClick: phoneNumSetters.showPurchaseInfo,
    onClose: phoneNumSetters.closePurchaseInfo,
    carNumValue: carNumStates.carNum,
    enteringTime: enteringTimeStates.enteringTime,
    price: enteringTimeStates.price,
    data: data
  }

  const paymentInfo = [
    { key: '차량번호', value: carNumStates.carNum },
    { key: '주차시간', value: generateParkingEndTime(enteringTimeStates.enteringTime) }
  ]

  const backIcon = <Icons.BackIcon stroke="currentColor" color="#263238" />
  return (
    <Cover
      id="layer3"
      isOpen={purchaseStates.isPurchase}
      className={`bottom-0 left-0 flex h-[100dvh] w-screen max-w-[768px] flex-1 flex-col`}
    >
      {purchaseStates.step === 0 && (
        <>
          <PurchaseHeader
            headerRef={purchaseStates.headerRef}
            text="약관 동의"
            onClick={headersHandlers.handleCloseClick}
            backIcon={backIcon}
            onClickClose={headersHandlers.clickHeaderCloseBtn}
          />
          <PurchaseTerms contentHeight={purchaseStates.contentHeight} footerRef={purchaseStates.footerRef} />
        </>
      )}
      {purchaseStates.step === 1 && enteringTimeStates.time && (
        <>
          <PurchaseHeader
            headerRef={purchaseStates.headerRef}
            text="주차 시간 선택"
            onClick={enteringTimeHandlers.onClickHeaderBackBtn}
            backIcon={backIcon}
            onClickClose={headersHandlers.clickHeaderCloseBtn}
          />
          <SharedEnteringTime
            time={enteringTimeStates.time}
            selectedIndex={enteringTimeStates.selectedIndex}
            onClickTimeValue={enteringTimeHandlers.handleEnteringTimeValue}
            isSharedNoticeOpen={enteringTimeStates.isSharedNoticeOpen}
            handleSharedNoticeOpen={enteringTimeHandlers.handleSharedNoticeOpen}
            onClickTimeNextBtn={enteringTimeHandlers.onClickTimeNextBtn}
            contentHeight={purchaseStates.contentHeight}
            enteringTime={enteringTimeStates.enteringTime}
            footerRef={purchaseStates.footerRef}
          />
        </>
      )}
      {purchaseStates.step === 2 && (
        <>
          <PurchaseHeader
            headerRef={purchaseStates.headerRef}
            text="차량번호 입력"
            onClick={carNumHandlers.onClickHeaderBackBtn}
            backIcon={backIcon}
            onClickClose={headersHandlers.clickHeaderCloseBtn}
          />
          <PurchaseCarNum
            value={carNumStates.carNum}
            onChange={carNumHandlers.onChangeCarNumValue}
            placeholder="서울12가3456, 12가4567"
            contentHeight={purchaseStates.contentHeight}
            disabled={!carNumHandlers.validateCarNum(carNumStates.carNum)}
            footerRef={purchaseStates.footerRef}
            handleCarNumNextBtn={carNumHandlers.handleCarNumNextBtn}
          />
        </>
      )}
      {purchaseStates.step === 3 && (
        <>
          <PurchaseHeader
            headerRef={purchaseStates.headerRef}
            text="휴대폰 번호 인증"
            onClick={phoneNumHandlers.onClickHeaderBackBtn}
            backIcon={backIcon}
            onClickClose={headersHandlers.clickHeaderCloseBtn}
          />
          <PurchasePhoneNum
            phoneValue={phoneNumStates.phoneNum}
            onChangePhoneNum={phoneNumSetters.onChangePhoneNumValue}
            authNumberValue={phoneNumStates.authNum}
            onChangeAuthNum={phoneNumSetters.onChangeAuthNumValue}
            disabled={
              phoneNumStates.isSend ? !(phoneNumStates.authNum === '0000') : !(phoneNumStates.phoneNum.length === 11)
            }
            onClick={phoneNumStates.isSend ? phoneNumSetters.onClickPhoneNextBtn : phoneNumSetters.handleSendAuthCode}
            isSend={phoneNumStates.isSend}
            isAuth={phoneNumStates.isAuth}
            isValidate={phoneNumStates.isValidate}
            onClickRetryAuthCode={phoneNumSetters.requestAuthCodeAgain}
            autoFocus={phoneNumStates.autoFocus}
            seconds={phoneNumStates.seconds}
            expirationTime={phoneNumHandlers.expirationTime(phoneNumStates.seconds)}
            errorText={phoneNumStates.errorText}
            contentHeight={purchaseStates.contentHeight}
            footerRef={purchaseStates.footerRef}
            requestAuthCodeAgain={phoneNumSetters.requestAuthCodeAgain}
            onClickPhoneNextBtn={phoneNumSetters.onClickPhoneNextBtn}
            handleSendAuthCode={phoneNumSetters.handleSendAuthCode}
          />
          {/* 결제정보확인창 */}
          <PurchaseConfirmInfoView props={confirmInfoProps} />
        </>
      )}
      {purchaseStates.step === 4 && (
        <PurchasePayment
          ticketName={data.basic.name}
          paymentInfo={paymentInfo}
          price={enteringTimeStates.price}
          paymentMethods={purchaseStates.paymentMethodList}
          onChangePaymentMethod={purchaseHandlers.radioHandler}
          onClickBack={purchaseHandlers.onClickHeaderBackBtn}
          onClickPayment={purchaseHandlers.onClickPayment}
          onCloseClick={headersHandlers.clickHeaderCloseBtn}
        />
      )}

      {/* 결제 성공 & 실패 */}
      {purchaseStates.isComplete && (
        <PurchaseCompleteView
          isComplete={purchaseStates.isComplete}
          data={data}
          time={enteringTimeStates.enteringTime}
          carNum={carNumStates.carNum}
          onClick={purchaseHandlers.onClickShowTicketBtn}
        />
      )}
      {purchaseStates.isInComplete && (
        <PurchaseInComplete isInComplete={purchaseStates.isInComplete} onClick={purchaseHandlers.onClickRePayment} />
      )}

      {/* 결제창 모바일 & 웹 */}
      {purchaseStates.frameUrl && isMobile && <PurchaseIframe url={purchaseStates.frameUrl} />}
      {purchaseStates.frameUrl && purchaseStates.isOpen && (
        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
          <iframe src={purchaseStates.frameUrl} className="z-100 h-full w-[768px]" scrolling="no" />
        </div>
      )}
      {phoneNumStates.isToastOpen && (
        <Toast
          text="인증 번호를 다시 전송했습니다."
          onClose={() => {
            phoneNumSetters.setIsToastOpen(false)
          }}
        />
      )}
      {phoneNumStates.isErrorToast && (
        <Toast
          text={phoneNumStates.toastText}
          onClose={() => {
            phoneNumSetters.setIsErrorToast(false)
          }}
          position="top"
        />
      )}
      <Modal
        targetId="#layer3"
        title={confirmTitle}
        isOpen={headersStates.isStopModalOpen}
        onClose={headersHandlers.clickFooterContinueBtn}
        footer={footer}
      >
        <Confirm
          bodyText="화면을 나갈 경우 작성한 정보들이 모두 사라집니다."
          className="mb-5 break-keep px-2 text-center"
        />
      </Modal>
    </Cover>
  )
}

export default SharedParkinglotPurchase
